import { updateUserSession, useTenants } from '@/lib/auth-service';
import { useRouter } from 'next/router';
import { usePostHog } from 'posthog-js/react';
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { cleanTypeName, updateUrlParam } from '@/lib/utils';
import { useLazyQuery, useQuery } from '@apollo/client';
import { gql } from '@/__generated__';
import {
  AvailableTeamMembersEnum,
  Media,
  OnboardingStatusEnum,
} from '@/__generated__/graphql';

type DayRange = {
  daysAhead?: number;
  daysBack?: number;
};
export interface IOrganizationFeedbackContext {
  dayRange?: DayRange;
  organizationId?: string;
  organizationName?: string;
  organizationLogos?: Partial<Media>[] | null;
  isGrantUser?: boolean;
  isMaggieUser?: boolean;
  isChangingOrganization?: boolean;
  setOrganizationId?: (organizationId: string) => void;
  organizationList?: { name?: string; id?: string }[];
}

interface OrganizationProviderProps {
  children: ReactNode;
}

const OrganizationQuery = gql(`
  query TeamMembers($input: OrganizationGetByIdInput) {
    organization {
      getById(input: $input) {
        ... on OrganizationGetByIdSuccess {
          result {
            name
            postReview {
              daysAhead
              daysBack
            }
            teamMembers {
              name
              status
            }
            logos {
              url
            }
          }
        }
      }
    }
  }
`);

const OrganizationsQuery = gql(`
  query GetOrganizationNames($input: OrganizationGetNamesInput!) {
    organization {
      getOrganizationNames(input: $input) {
        ... on OrganizationGetNamesSuccess {
          organizations {
            name
            id
          }
        }
      }
    }
  }
`);

const defaultState: IOrganizationFeedbackContext = {};

const OrganizationContext =
  createContext<IOrganizationFeedbackContext>(defaultState);

export const useOrganizationContext = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      'useOrganizationContext must be used within a OrganizationProvider'
    );
  }
  return context;
};

export const OrganizationProvider: FC<OrganizationProviderProps> = ({
  children,
}) => {
  const router = useRouter();
  const [organizationIdInternal, setOrganizationIdInternal] = useState<string>(
    () => {
      return (router.query.organizationId as string) || '';
    }
  );
  const [organizationName, setOrganizationName] = useState<string>('');
  const [organizationLogos, setOrganizationLogos] =
    useState<Partial<Media>[]>();
  const [dayRange, setDayRange] = useState<DayRange>();
  const [isGrantUser, setIsGrantUser] = useState<boolean>();
  const [isMaggieUser, setIsMaggieUser] = useState<boolean>();
  const [isChangingOrganization, setIsChangingOrganization] =
    useState<boolean>();
  const { tenants, isLoading } = useTenants();
  const { data: organizationData, loading: isOrganizationDataLoading } =
    useQuery(OrganizationsQuery, {
      variables: { input: { ids: tenants } },
      skip: isLoading || !tenants.length,
    });
  const posthog = usePostHog();
  const [getOrganizationTeamMembers] = useLazyQuery(OrganizationQuery);

  useEffect(() => {
    if (isLoading || isOrganizationDataLoading) return;
    if (router.query.organizationId) {
      if (tenants.includes(router.query.organizationId)) {
        setOrganizationId(router.query.organizationId as string);
      } else {
        setOrganizationId('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.organizationId, isLoading, isOrganizationDataLoading]);

  const setOrganizationId = useCallback(
    async (orgId: string) => {
      const currentOrg =
        organizationData?.organization.getOrganizationNames.organizations?.find(
          (org) => org?.id === orgId
        );
      setIsChangingOrganization(true);
      setOrganizationIdInternal(orgId);
      await updateUserSession(orgId);
      posthog?.group('company', orgId, {
        name: currentOrg?.name,
        id: currentOrg?.id,
      });
      // updateUrlParam('organizationId', orgId);
      const teamMembers = await getOrganizationTeamMembers({
        variables: { input: { id: orgId } },
      });

      if (
        teamMembers.data?.organization?.getById?.__typename ===
        'OrganizationGetByIdSuccess'
      ) {
        setOrganizationName(teamMembers.data.organization.getById.result.name);
        setOrganizationLogos(
          cleanTypeName(teamMembers?.data?.organization?.getById?.result?.logos)
        );
        setDayRange({
          daysAhead:
            teamMembers.data?.organization.getById?.result.postReview
              ?.daysAhead,
          daysBack:
            teamMembers.data?.organization.getById?.result.postReview?.daysBack,
        });
        setIsGrantUser(
          teamMembers.data.organization.getById.result.teamMembers?.some(
            (member) =>
              member.name === AvailableTeamMembersEnum.Grant &&
              member.status === OnboardingStatusEnum.Done
          )
        );
        setIsMaggieUser(
          teamMembers.data.organization.getById.result.teamMembers?.some(
            (member) =>
              member.name === AvailableTeamMembersEnum.Maggie &&
              member.status === OnboardingStatusEnum.Done
          )
        );
      }
      setIsChangingOrganization(false);
    },
    [
      getOrganizationTeamMembers,
      posthog,
      organizationData?.organization?.getOrganizationNames?.organizations,
    ]
  );

  useEffect(() => {
    if (
      organizationData?.organization?.getOrganizationNames?.organizations
        ?.length === 1
    ) {
      const onlyOrgId =
        organizationData?.organization?.getOrganizationNames?.organizations[0]
          ?.id;
      if (onlyOrgId && setOrganizationId) {
        setOrganizationId(onlyOrgId);
      }
    }
  }, [
    organizationData?.organization?.getOrganizationNames?.organizations,
    setOrganizationId,
  ]);

  useEffect(() => {
    if (organizationIdInternal) {
      updateUrlParam('organizationId', organizationIdInternal);
      // router.query.organizationId = organizationIdInternal;
      // router.push(router);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationIdInternal]);

  return (
    <OrganizationContext.Provider
      value={{
        dayRange,
        organizationId: organizationIdInternal,
        organizationName,
        organizationLogos,
        isGrantUser,
        isMaggieUser,
        isChangingOrganization,
        setOrganizationId,
        organizationList:
          organizationData?.organization?.getOrganizationNames?.organizations
            ?.filter(Boolean)
            .map((org) => ({ name: org?.name, id: org?.id })) || [],
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};
